/**
 * deriveWinner — given a game, return the winning player (or null).
 *
 * Pure + RN-free so the lobby card and anything else can share one
 * source of truth and it stays unit-testable.
 *
 * Resolution order:
 *   1. The immutable completion snapshot carries the
 *      authoritative winner slot for completed games.
 *   2. The engine's `game-end` event in state.events carries the
 *      authoritative winner slot — map it back to the player.
 *   3. GamePlayer.rank, persisted once at completion (rank 1 = winner).
 *      This is the ONLY path available on the trimmed /games list payload,
 *      which omits state entirely — without it the home screen's completed
 *      tiles could never show a winner.
 *   4. Fallback for stalled finishes that marked COMPLETE without a
 *      clean game-end event or ranks: if exactly one player is NOT
 *      eliminated, that lone survivor is the winner.
 * Returns null for non-complete games or when no path resolves.
 */
import { resignedSlotIds, type GameResponse, type GamePlayerResponse, type GameResultSnapshotPlacement, type TeamId } from '@warcarp/api-types'

export function deriveWinner(game: GameResponse): GamePlayerResponse | null {
  if (game.status !== 'COMPLETE') return null

  const snapshotWinnerSlot = game.resultSnapshot?.winnerSlot
  if (snapshotWinnerSlot !== null && snapshotWinnerSlot !== undefined) {
    const winner = game.players.find((p) => p.slot === snapshotWinnerSlot)
    if (winner) return winner
  }

  const state = game.state as {
    events?: Array<{ kind: string; winner?: string | null }>
    eliminated?: string[]
  } | null
  const events = state?.events ?? []
  const eliminated = state?.eliminated ?? []

  const endEvt = events.find((e) => e.kind === 'game-end')
  const winnerSlot = endEvt?.winner ?? null
  if (winnerSlot) {
    const winner = game.players.find((p) => String(p.slot) === winnerSlot)
    if (winner) return winner
  }

  const ranked = game.players.find((p) => p.rank === 1)
  if (ranked) return ranked

  const survivors = game.players.filter((p) => !eliminated.includes(String(p.slot)))
  return survivors.length === 1 ? survivors[0]! : null
}

/**
 * A team game's winners: the team and its members, eliminated ones included
 * but not those who resigned (a resignation is a loss even when the team wins).
 */
export interface WinningTeam {
  team: TeamId
  /** Every winning seat, in turn order where known. */
  members: GamePlayerResponse[]
}

interface TeamResultState {
  teams?: Record<string, TeamId>
  players?: string[]
  eliminated?: string[]
  resigned?: string[]
  events?: Array<{ kind: string; winner?: string | null; winnerTeam?: TeamId | null; winners?: string[]; player?: string }>
}

/**
 * The winning team of a completed team game, or null (free-for-all, a tie,
 * or not complete). Reads, in order: the completion snapshot's
 * `winnerTeam`/`winnerSlots`, the engine's `game-end` event
 * (`winnerTeam`/`winners`), then the team of the players still standing.
 * Every member is a winner, including members eliminated earlier, except
 * members who resigned (plan §12 decision 7). The snapshot's `winnerSlots`
 * and the event's `winners` already leave them out; the fallbacks drop them
 * here.
 */
export function deriveWinningTeam(input: {
  gameStatus: string
  gamePlayers: GamePlayerResponse[]
  gameState: TeamResultState | null | undefined
  winnerTeam?: TeamId | null
  winnerSlots?: readonly number[] | null
}): WinningTeam | null {
  const { gameStatus, gamePlayers, gameState } = input
  if (gameStatus !== 'COMPLETE') return null
  const teams = gameState?.teams
  const order = gameState?.players ?? []
  const turnPos = (p: GamePlayerResponse) => {
    const i = order.indexOf(String(p.slot))
    return i === -1 ? Number.MAX_SAFE_INTEGER : i
  }
  const sorted = (members: GamePlayerResponse[]) => [...members].sort((a, b) => turnPos(a) - turnPos(b))
  const resigned = resignedSlotIds(gameState as Parameters<typeof resignedSlotIds>[0])
  const ofTeam = (team: TeamId) => teams
    ? gamePlayers.filter((p) => teams[String(p.slot)] === team && !resigned.has(String(p.slot)))
    : []

  if (input.winnerTeam) {
    const slots = input.winnerSlots ?? []
    const members = slots.length > 0 ? gamePlayers.filter((p) => slots.includes(p.slot)) : ofTeam(input.winnerTeam)
    return { team: input.winnerTeam, members: sorted(members) }
  }
  // The snapshot says "no team won" (a tie) when it carries the key as null.
  if (input.winnerTeam === null) return null
  if (!teams) return null

  const end = gameState?.events?.find((e) => e.kind === 'game-end')
  if (end?.winnerTeam) {
    const winners = end.winners ?? []
    const members = winners.length > 0 ? gamePlayers.filter((p) => winners.includes(String(p.slot))) : ofTeam(end.winnerTeam)
    return { team: end.winnerTeam, members: sorted(members) }
  }
  if (end && end.winnerTeam === null) return null

  // Fallback: the one team that still has a player standing.
  const eliminated = gameState?.eliminated ?? []
  const standing = new Set(order.filter((p) => !eliminated.includes(p)).map((p) => teams[p]).filter(Boolean))
  if (standing.size !== 1) return null
  const team = [...standing][0]!
  return { team, members: sorted(ofTeam(team)) }
}

/**
 * `deriveWinningTeam` for a game payload: the full game (snapshot, state) or a
 * home-list row, which carries no state but a finished team game's
 * `winnerTeam` / `winnerSlots`.
 */
export function deriveWinningTeamForGame(game: GameResponse): WinningTeam | null {
  const snapshot = game.resultSnapshot?.winnerTeam !== undefined ? game.resultSnapshot : null
  return deriveWinningTeam({
    gameStatus: game.status,
    gamePlayers: game.players,
    gameState: game.state as TeamResultState | null,
    winnerTeam: snapshot ? snapshot.winnerTeam : game.winnerTeam,
    winnerSlots: snapshot ? snapshot.winnerSlots : game.winnerSlots,
  })
}

/** One team's row in a team game's final standings. */
export interface TeamStanding {
  team: TeamId
  placement: number
  members: GameResultSnapshotPlacement[]
}

/**
 * Team standings from the snapshot placements: teammates share a placement,
 * so each team appears once, best first. Empty when the placements carry no
 * teams (free-for-all).
 */
export function teamStandings(placements: readonly GameResultSnapshotPlacement[] | undefined): TeamStanding[] {
  const out: TeamStanding[] = []
  for (const p of placements ?? []) {
    if (!p.team) continue
    const row = out.find((r) => r.team === p.team)
    if (row) {
      row.members.push(p)
      row.placement = Math.min(row.placement, p.placement)
    } else {
      out.push({ team: p.team, placement: p.placement, members: [p] })
    }
  }
  return out.sort((a, b) => a.placement - b.placement || a.team.localeCompare(b.team))
}
